import React from 'react';
import classNames from 'classnames';
import { DNSValidationState, BusinessNameValidationState } from '../types';
import { getAppDomainSuffix, getFullAppDomain } from '../config/endpoints';

interface DNSSubdomainFieldProps {
  id: string;
  name: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  validation: DNSValidationState | BusinessNameValidationState;
  error?: string;
  required?: boolean;
  autoComplete?: string;
  placeholder?: string;
  helpText?: string;
}

export const DNSSubdomainField: React.FC<DNSSubdomainFieldProps> = ({
  id,
  name,
  label,
  value,
  onChange,
  validation,
  error,
  required = false,
  autoComplete,
  placeholder,
  helpText,
}) => {
  // A check that could not be completed is a warning, not a rejection: the name
  // may well be free, so it must not be styled or worded as "already taken".
  const checkFailed = validation.status === 'check_failed';
  const hasError = !!(
    error || (validation.isAvailable === false && !checkFailed && validation.message)
  );
  const isValid = validation.isAvailable === true;
  const isValidating = validation.isValidating;

  return (
    <div className="form-field">
      <label htmlFor={id} className="form-label">
        {label}
        {required && <span className="text-error-600 ml-1">*</span>}
      </label>
      
      <div className="relative max-w-4xl">
        <div className="flex flex-col space-y-2">
          <div className="flex">
            <div className="relative flex-1 min-w-0">
              <input
                type="text"
                id={id}
                name={name}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                {...(autoComplete && { autoComplete })}
                placeholder={placeholder}
                className={classNames(
                  'form-input',
                  'rounded-r-none border-r-0 w-full',
                  {
                    'border-error-300 focus:border-error-500 focus:ring-error-500': hasError,
                    'border-success-300 focus:border-success-500 focus:ring-success-500': isValid,
                    'pr-12': isValidating || isValid || hasError,
                  }
                )}
                aria-describedby={helpText ? `${id}-help` : undefined}
              />

              {/* Validation status icon - positioned inside input field */}
              {(isValidating || isValid || hasError) && (
                <div className="absolute inset-y-0 right-0 flex items-center pointer-events-none pr-3">
                  {isValidating && (
                    <div className="loading-spinner h-4 w-4"></div>
                  )}
                  {!isValidating && isValid && (
                    <svg className="h-5 w-5 text-success-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  )}
                  {!isValidating && hasError && (
                    <svg className="h-5 w-5 text-error-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              )}
            </div>

            {/* Domain suffix */}
            <div className={classNames(
              'px-4 py-2 border border-l-0 rounded-r-md bg-gray-50 text-gray-700 text-sm font-medium flex items-center',
              {
                'border-error-300': hasError,
                'border-success-300': isValid,
                'border-gray-300': !hasError && !isValid,
              }
            )}>
              <span className="whitespace-nowrap">{getAppDomainSuffix()}</span>
            </div>
          </div>

          {/* Full domain preview - only show when validation passes */}
          {value && isValid && (
            <div className="mt-2 text-sm text-gray-600 bg-gray-50 rounded-md p-3 border border-gray-200">
              <div className="font-medium text-gray-700 mb-1">Your portal address:</div>
              <div className="break-all font-mono text-sm text-gray-900">
                {getFullAppDomain(value)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Help text and validation feedback */}
      <div className="mt-1 space-y-1">
        {helpText && (
          <p id={`${id}-help`} className="form-help-text">
            {helpText}
          </p>
        )}
        
        {/* Validating message */}
        {isValidating && (
          <div className="flex items-center text-sm text-blue-600">
            <div className="loading-spinner h-4 w-4 mr-2"></div>
            <span>Checking business name availability...</span>
          </div>
        )}
        
        {/* Form validation error (takes precedence over DNS validation) */}
        {error && (
          <div className="validation-error">
            <div className="flex items-center">
              <svg className="h-4 w-4 text-error-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">{error}</span>
            </div>
          </div>
        )}

        {/* Could not complete the check - the customer may still continue */}
        {!error && !isValidating && checkFailed && validation.message && (
          <div className="flex items-start text-sm text-amber-700">
            <svg className="h-4 w-4 text-amber-600 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span>
              <span className="font-medium">{validation.message}</span>{' '}
              You can continue - we'll confirm this address when your account is set up.
            </span>
          </div>
        )}

        {/* Business name already taken message */}
        {!error && !isValidating && !checkFailed && validation.isAvailable === false && validation.message && (
          <div className="validation-error">
            <div className="flex items-center">
              <svg className="h-4 w-4 text-error-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">{validation.message}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
